import { get } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CamelCasedPropertiesDeep } from "type-fest";

import {
  AnalyticsActiveUsers,
  AnalyticsAudioSummary,
  AnalyticsBreakdownItem,
  AnalyticsFeedbackSummary,
  AnalyticsRequests,
  AnalyticsSummary,
  AnalyticsTopDocument,
  AnalyticsTopLanguagePair,
  AnalyticsTopUserRequests,
  AnalyticsTranslationSummary,
  GetAudioSummaryApiAnalyticsAudioSummaryGetData,
  GetBreakdownApiAnalyticsBreakdownGetData,
  GetFeedbackSummaryApiAnalyticsFeedbackGetData,
  GetSummaryApiAnalyticsSummaryGetData,
  GetTokenUsageApiAnalyticsTokenUsageGetData,
  GetTopApiAnalyticsTopGetData,
  TokenUsageSummary,
} from "@:openapi";

export type AnalyticsTop =
  | AnalyticsTopUserRequests[]
  | AnalyticsTopDocument[]
  | AnalyticsTopLanguagePair[];

export interface Analytics {
  GET_SUMMARY: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetSummaryApiAnalyticsSummaryGetData["query"]
    >;
    Response: AnalyticsSummary;
  };
  GET_TOP: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetTopApiAnalyticsTopGetData["query"]
    >;
    Response: AnalyticsTop;
  };
  GET_FEEDBACK: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetFeedbackSummaryApiAnalyticsFeedbackGetData["query"]
    >;
    Response: AnalyticsFeedbackSummary;
  };
  GET_TRANSLATION_SUMMARY: {
    RequestQuery: {
      botId?: string;
      organizationId?: string;
      fromDate: string;
      toDate: string;
    };
    Response: AnalyticsTranslationSummary;
  };
  GET_TOKEN_USAGE: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetTokenUsageApiAnalyticsTokenUsageGetData["query"]
    >;
    Response: TokenUsageSummary;
  };
  GET_BREAKDOWN: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetBreakdownApiAnalyticsBreakdownGetData["query"]
    >;
    Response: AnalyticsBreakdownItem[];
  };
  GET_AUDIO_SUMMARY: {
    RequestQuery: CamelCasedPropertiesDeep<
      GetAudioSummaryApiAnalyticsAudioSummaryGetData["query"]
    >;
    Response: AnalyticsAudioSummary;
  };
}

export const analyticsQueryKey = createQueryKeys("analytics", {
  getSummary: (params: Analytics["GET_SUMMARY"]["RequestQuery"]) => [params],
  getTop: (params: Analytics["GET_TOP"]["RequestQuery"]) => [params],
  getFeedback: (params: Analytics["GET_FEEDBACK"]["RequestQuery"]) => [params],
  getTranslationSummary: (
    params: Analytics["GET_TRANSLATION_SUMMARY"]["RequestQuery"],
  ) => [params],
  getTokenUsage: (params: Analytics["GET_TOKEN_USAGE"]["RequestQuery"]) => [
    params,
  ],
  getBreakdown: (params: Analytics["GET_BREAKDOWN"]["RequestQuery"]) => [
    params,
  ],
  getAudioSummary: (params: Analytics["GET_AUDIO_SUMMARY"]["RequestQuery"]) => [
    params,
  ],
});

export type AnalyticsSummarySelector = {
  activeUsersPeriod: number;
  activeUsersPeriodDistinct: number;
  activeUsersPeriodData: AnalyticsActiveUsers[];
  requestsPeriod: number;
  requestsPeriodData: AnalyticsRequests[];
};

export const useQueryAnalyticsSummary = (
  params: Analytics["GET_SUMMARY"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<
      Analytics["GET_SUMMARY"]["Response"],
      AxiosError,
      AnalyticsSummarySelector
    >,
    "queryKey"
  >,
) => {
  return useQuery<
    Analytics["GET_SUMMARY"]["Response"],
    AxiosError,
    AnalyticsSummarySelector
  >({
    queryKey: analyticsQueryKey.getSummary(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_SUMMARY"]["Response"]>(
        `/api/analytics/summary`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsTop = (
  params: Analytics["GET_TOP"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_TOP"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_TOP"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getTop(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_TOP"]["Response"]>(
        `/api/analytics/top`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsFeedback = (
  params: Analytics["GET_FEEDBACK"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_FEEDBACK"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_FEEDBACK"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getFeedback(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_FEEDBACK"]["Response"]>(
        `/api/analytics/feedback`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsTokenUsage = (
  params: Analytics["GET_TOKEN_USAGE"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_TOKEN_USAGE"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_TOKEN_USAGE"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getTokenUsage(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_TOKEN_USAGE"]["Response"]>(
        `/api/analytics/token-usage`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsBreakdown = (
  params: Analytics["GET_BREAKDOWN"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_BREAKDOWN"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_BREAKDOWN"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getBreakdown(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_BREAKDOWN"]["Response"]>(
        `/api/analytics/breakdown`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsAudioSummary = (
  params: Analytics["GET_AUDIO_SUMMARY"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<Analytics["GET_AUDIO_SUMMARY"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_AUDIO_SUMMARY"]["Response"], AxiosError>({
    queryKey: analyticsQueryKey.getAudioSummary(params).queryKey,
    queryFn: () => {
      return get<Analytics["GET_AUDIO_SUMMARY"]["Response"]>(
        `/api/analytics/audio-summary`,
        params,
      );
    },
    ...options,
  });
};

export const useQueryAnalyticsTranslationSummary = (
  params: Analytics["GET_TRANSLATION_SUMMARY"]["RequestQuery"],
  options?: Omit<
    UseQueryOptions<
      Analytics["GET_TRANSLATION_SUMMARY"]["Response"],
      AxiosError
    >,
    "queryKey"
  >,
) => {
  return useQuery<Analytics["GET_TRANSLATION_SUMMARY"]["Response"], AxiosError>(
    {
      queryKey: analyticsQueryKey.getTranslationSummary(params).queryKey,
      queryFn: () => {
        return get<Analytics["GET_TRANSLATION_SUMMARY"]["Response"]>(
          `/api/analytics/translation-summary`,
          params,
        );
      },
      ...options,
    },
  );
};
